import { HttpClient } from "@angular/common/http";
import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Firm360HttpContext } from "@firm360/libs/api/http";
import type * as Ably from "ably";
import { firstValueFrom, Subject, of } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { AccountService } from "../../core/auth/account.service";
import {
    AblyClientFactory,
    type RealtimeChannel,
    type RealtimeConnection,
    type RealtimeConnectionState,
} from "./ably-client.factory";
import type { IRealtimeToken } from "./realtime.model";

type RealtimeMessageListener = (data: unknown) => void;

@Injectable({ providedIn: "root" })
export class RealtimeService {
    private static readonly INITIAL_RETRY_DELAY_MS = 5_000;
    private static readonly MAX_RETRY_DELAY_MS = 60_000;
    private readonly http = inject(HttpClient);
    private readonly clientFactory = inject(AblyClientFactory);
    private readonly accountService = inject(AccountService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly tokenUrl = `${environment.apiUrl}api/realtime/token`;

    private readonly listeners = new Map<
        string,
        Set<RealtimeMessageListener>
    >();
    private readonly attachedEvents = new Set<string>();
    private readonly stop$ = new Subject<void>();
    private readonly _ready$ = new Subject<void>();
    readonly ready$ = this._ready$.asObservable();

    private connection: RealtimeConnection | null = null;
    private channel: RealtimeChannel | null = null;
    private removeStateListener: (() => void) | null = null;
    private started = false;
    private authenticated = false;
    private connectionGeneration = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectDelayMs = RealtimeService.INITIAL_RETRY_DELAY_MS;
    private connectionInterrupted = false;
    private authenticatedUserId: unknown = null;

    constructor() {
        this.accountService.userIdentity
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((identity) => {
                const userId = identity?.id ?? null;
                if (
                    this.authenticatedUserId !== null &&
                    userId !== this.authenticatedUserId
                ) {
                    this.stop();
                }
                this.authenticatedUserId = userId;
                this.authenticated = Boolean(identity);
                if (this.authenticated && this.listeners.size > 0) {
                    this.ensureStarted();
                } else if (!this.authenticated) {
                    this.stop();
                }
            });
        this.destroyRef.onDestroy(() => this.stop());
    }

    subscribe(
        eventName: string,
        listener: RealtimeMessageListener,
    ): () => void {
        const eventListeners = this.listeners.get(eventName) ?? new Set();
        eventListeners.add(listener);
        this.listeners.set(eventName, eventListeners);

        if (this.channel) {
            const generation = this.connectionGeneration;
            void this.attachEvent(eventName, generation).catch((error) =>
                this.handleSetupFailure(generation, error),
            );
        }
        if (this.authenticated) {
            this.ensureStarted();
        }

        return () => {
            const currentListeners = this.listeners.get(eventName);
            currentListeners?.delete(listener);
            if (!currentListeners?.size) {
                this.listeners.delete(eventName);
                if (this.attachedEvents.delete(eventName)) {
                    this.channel?.unsubscribe(eventName);
                }
            }
        };
    }

    private ensureStarted(): void {
        if (this.started) {
            return;
        }
        this.started = true;
        this.requestTokenAndConnect();
    }

    private requestTokenAndConnect(): void {
        const requestGeneration = this.connectionGeneration;
        this.getToken()
            .pipe(
                takeUntil(this.stop$),
                takeUntilDestroyed(this.destroyRef),
                catchError((error: unknown) => {
                    console.warn("Realtime token unavailable", error);
                    return of(null);
                }),
            )
            .subscribe((token) => {
                if (
                    !this.started ||
                    requestGeneration !== this.connectionGeneration
                ) {
                    return;
                }
                if (!token) {
                    this.scheduleReconnect();
                    return;
                }
                if (!token.enabled) {
                    return;
                }
                if (!token.channel || !token.tokenRequest) {
                    this.scheduleReconnect();
                    return;
                }
                const initialToken = this.parseTokenRequest(token.tokenRequest);
                if (!initialToken) {
                    this.scheduleReconnect();
                    return;
                }
                this.openConnection(
                    token.channel,
                    initialToken,
                    requestGeneration,
                );
            });
    }

    private openConnection(
        channelName: string,
        initialToken: Ably.TokenRequest,
        requestGeneration: number,
    ): void {
        if (!this.started || requestGeneration !== this.connectionGeneration) {
            return;
        }
        this.closeConnection();
        const generation = this.connectionGeneration;
        this.clientFactory
            .create(initialToken, () => this.fetchTokenRequest())
            .then((connection) => {
                if (generation !== this.connectionGeneration) {
                    connection.close();
                    return;
                }
                this.connection = connection;
                this.removeStateListener = connection.onStateChange((state) =>
                    this.handleConnectionState(generation, state),
                );
                this.channel = connection.getChannel(channelName);
                return Promise.all(
                    [...this.listeners.keys()].map((eventName) =>
                        this.attachEvent(eventName, generation),
                    ),
                );
            })
            .then((attached) => {
                if (
                    attached &&
                    generation === this.connectionGeneration &&
                    this.started
                ) {
                    this.reconnectDelayMs =
                        RealtimeService.INITIAL_RETRY_DELAY_MS;
                    this._ready$.next();
                }
            })
            .catch((error: unknown) =>
                this.handleSetupFailure(generation, error),
            );
    }

    private attachEvent(eventName: string, generation: number): Promise<void> {
        if (
            generation !== this.connectionGeneration ||
            !this.channel ||
            this.attachedEvents.has(eventName)
        ) {
            return Promise.resolve();
        }
        this.attachedEvents.add(eventName);
        return this.channel
            .subscribe(eventName, (message) => {
                for (const listener of this.listeners.get(eventName) ?? []) {
                    listener(message.data);
                }
            })
            .catch((error: unknown) => {
                this.attachedEvents.delete(eventName);
                throw error;
            });
    }

    private handleConnectionState(
        generation: number,
        state: RealtimeConnectionState,
    ): void {
        if (generation !== this.connectionGeneration || !this.started) {
            return;
        }
        if (state === "connected") {
            if (this.connectionInterrupted) {
                this.connectionInterrupted = false;
                this._ready$.next();
            }
            return;
        }
        if (state === "disconnected") {
            this.connectionInterrupted = true;
            return;
        }
        if (state === "suspended" || state === "failed" || state === "closed") {
            this.connectionInterrupted = true;
            this.closeConnection();
            this.scheduleReconnect();
        }
    }

    private handleSetupFailure(generation: number, error: unknown): void {
        if (generation !== this.connectionGeneration) {
            return;
        }
        console.warn("Realtime connection unavailable", error);
        this.closeConnection();
        this.scheduleReconnect();
    }

    private fetchTokenRequest(): Promise<Ably.TokenRequest> {
        return firstValueFrom(
            this.getToken().pipe(
                map((token) => {
                    if (!token.enabled || !token.tokenRequest) {
                        throw new Error("Realtime token unavailable");
                    }
                    const parsed = this.parseTokenRequest(token.tokenRequest);
                    if (!parsed) {
                        throw new Error("Realtime token invalid");
                    }
                    return parsed;
                }),
            ),
        );
    }

    private getToken() {
        return this.http.post<IRealtimeToken>(
            this.tokenUrl,
            {},
            { context: new Firm360HttpContext().throwErrors() },
        );
    }

    private parseTokenRequest(value: string): Ably.TokenRequest | null {
        try {
            return JSON.parse(value) as Ably.TokenRequest;
        } catch {
            return null;
        }
    }

    private stop(): void {
        this.stop$.next();
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.closeConnection();
        this.reconnectDelayMs = RealtimeService.INITIAL_RETRY_DELAY_MS;
        this.connectionInterrupted = false;
        this.started = false;
    }

    private scheduleReconnect(): void {
        if (!this.started || this.reconnectTimer) {
            return;
        }
        const delay = this.reconnectDelayMs;
        this.reconnectDelayMs = Math.min(
            delay * 2,
            RealtimeService.MAX_RETRY_DELAY_MS,
        );
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (this.started) {
                this.requestTokenAndConnect();
            }
        }, delay);
    }

    private closeConnection(): void {
        this.connectionGeneration++;
        this.removeStateListener?.();
        this.removeStateListener = null;
        try {
            this.channel?.unsubscribe();
        } catch {
            // best-effort teardown
        }
        try {
            this.connection?.close();
        } catch {
            // best-effort teardown
        }
        this.attachedEvents.clear();
        this.channel = null;
        this.connection = null;
    }
}
